import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo, useRef } from 'react'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  AQSI_WINDOWS,
  DiscrepanciesScreen,
  FEEDBACK_SCOPES,
  IncidentsScreen,
  RecoveryProofScreen,
  RefundLookupScreen,
  RefundScreen,
  SlipRefundScreen,
  aqsiReceipt,
  aqsiReceiptDiscrepancies,
  aqsiReceiptRegisters,
  aqsiRecoveryProof,
  aqsiRefund,
  aqsiRefundSeat,
  aqsiSaleIncidents,
  aqsiSaleOperation,
  aqsiSlipRefundSeat,
  aqsiSlipRefundSend,
  aqsiSlipRefundSlips,
  buildSlipRefundVm,
  createDiscrepanciesActions,
  createIncidentsActions,
  createRecoveryProofActions,
  createRefundActions,
  discrepancyLimitOf,
  finishedRefundOf,
  incidentsCursorOf,
  incidentsSeatOf,
  incidentsViewOf,
  makeSelectDiscrepanciesVm,
  makeSelectIncidentsVm,
  makeSelectRecoveryProofVm,
  makeSelectRefundVm,
  mineSeatOf,
  newCommandIntent,
  refundCommandOf,
  refundDraftOf,
  refundOperationOf,
  ridKey,
  useSaleProgress,
} from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { useMySession } from './useMySession'

/**
 * Окно возврата по чеку. rid и номер чека — из контекста окна (адрес или `?w=`),
 * не из «текущего ресурса»: переключение кабинета в шапке не меняет контекст
 * открытого возврата. Возврат — из `sales/active` кассы (сервер), не из хранилища.
 */
export function AqsiRefundBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const receiptId = window.params.key ?? ''
  const skip = resourceId === '' || receiptId === ''
  const args = useMemo(() => ({ resourceId }), [resourceId])
  const receiptArgs = useMemo(() => ({ resourceId, receiptId }), [receiptId, resourceId])

  aqsiReceipt.useQuerySubscription(receiptArgs, { skip })
  // «Возвраты посчитаны» — признак журнала этого rid (`capabilities.refunds`), как у детализации чека.
  aqsiReceiptRegisters.useQuerySubscription(args, { skip })
  const { select: selectMySession, session: mine } = useMySession(resourceId)
  const seat = mineSeatOf(mine)
  const registerKey = seat?.registerKey ?? null
  const deviceId = seat?.deviceId ?? null
  const sessionVersion = mine?.version ?? null
  const seatArgs = useMemo(
    () => (registerKey === null || deviceId === null ? null : { resourceId, registerKey, deviceId }),
    [deviceId, resourceId, registerKey],
  )
  aqsiRefundSeat.useQuerySubscription(seatArgs ?? skipToken)

  const selectSeat = useMemo(() => (seatArgs === null ? null : aqsiRefundSeat.select(seatArgs)), [seatArgs])
  const operationId = refundOperationOf(useAppSelector((state: RootState) => selectSeat?.(state).data))
  const operationArgs = useMemo(
    () => (operationId === null ? null : { resourceId, operationId }),
    [operationId, resourceId],
  )
  aqsiSaleOperation.useQuerySubscription(operationArgs ?? skipToken)
  const finishedRefundId = useAppSelector((state: RootState) => finishedRefundOf(state.aqsi.refunds, receiptArgs))
  const finishedArgs = useMemo(
    () => (finishedRefundId === null ? null : { resourceId, refundId: finishedRefundId }),
    [finishedRefundId, resourceId],
  )
  aqsiRefund.useQuerySubscription(finishedArgs ?? skipToken)

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectRefundVm()
    const selectReceipt = aqsiReceipt.select(receiptArgs)
    const selectReceiptRegisters = aqsiReceiptRegisters.select(args)
    const selectOperation = operationArgs === null ? null : aqsiSaleOperation.select(operationArgs)
    const selectFinished = finishedArgs === null ? null : aqsiRefund.select(finishedArgs)
    const feedbackKey = ridKey(resourceId, FEEDBACK_SCOPES.refund(receiptId))
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        receiptId,
        receipt: selectReceipt(state),
        mySession: selectMySession(state),
        seat: selectSeat?.(state),
        operation: selectOperation?.(state),
        finished: selectFinished?.(state),
        finishedRefundId: finishedRefundOf(state.aqsi.refunds, receiptArgs),
        refundsKnown: selectReceiptRegisters(state).data?.state.refundsKnown,
        draft: refundDraftOf(state.aqsi.refunds, receiptArgs),
        command: seatArgs === null ? undefined : refundCommandOf(state.aqsi.refundCommands, seatArgs),
        feedback: state.aqsi.feedback.byKey[feedbackKey],
      })
  }, [args, finishedArgs, operationArgs, receiptArgs, receiptId, resourceId, seatArgs, selectMySession, selectSeat])
  const vm = useAppSelector(selectVm)

  // Ход операции возврата — сверкой ЧТЕНИЕМ, как у продажи; POST к кассе отсюда не уходит.
  useSaleProgress(resourceId, seatArgs, vm.operation, dispatch)

  const actions = useMemo(
    () =>
      createRefundActions(
        vm,
        registerKey === null || deviceId === null || sessionVersion === null ? null : { registerKey, deviceId, sessionVersion },
        dispatch,
        {
          openRmk: () => openWindow(AQSI_WINDOWS.rmk, { rid: resourceId }),
          openSale: () => openWindow(AQSI_WINDOWS.rmk, { rid: resourceId }),
          openRefund: (other) => window.replaceWith(AQSI_WINDOWS.refund, { rid: resourceId, key: other }),
          openResolve: (id) => openWindow(AQSI_WINDOWS.saleResolve, { rid: resourceId, operationId: id }),
          close: window.close,
        },
      ),
    [deviceId, dispatch, openWindow, resourceId, sessionVersion, vm, window, registerKey],
  )
  return <RefundScreen vm={vm} actions={actions} />
}

/** Журнал инцидентов моего места: место — из касс этого rid (журнал читает тот, кто за кассой). */
export function AqsiIncidentsBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const { select: selectMySession, session: mine } = useMySession(resourceId)
  const seat = incidentsSeatOf(mine)
  const registerKey = seat?.registerKey ?? null
  const deviceId = seat?.deviceId ?? null
  const cursor = useAppSelector((state: RootState) =>
    registerKey === null ? null : incidentsCursorOf(incidentsViewOf(state.aqsi.incidents, { resourceId, registerKey })),
  )
  const pageArgs = useMemo(
    () => (registerKey === null || deviceId === null ? null : { resourceId, registerKey, deviceId, cursor }),
    [cursor, deviceId, resourceId, registerKey],
  )
  aqsiSaleIncidents.useQuerySubscription(pageArgs ?? skipToken)

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectIncidentsVm()
    const selectPage = pageArgs === null ? null : aqsiSaleIncidents.select(pageArgs)
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        mySession: selectMySession(state),
        page: selectPage?.(state),
        view:
          pageArgs === null
            ? incidentsViewOf(state.aqsi.incidents, { resourceId, registerKey: '' })
            : incidentsViewOf(state.aqsi.incidents, pageArgs),
      })
  }, [pageArgs, resourceId, selectMySession])
  const vm = useAppSelector(selectVm)
  const actions = useMemo(
    () =>
      createIncidentsActions(vm, dispatch, {
        openProof: (saleId) => openWindow(AQSI_WINDOWS.recoveryProof, { rid: resourceId, saleId }),
        openRmk: () => openWindow(AQSI_WINDOWS.rmk, { rid: resourceId }),
      }),
    [dispatch, openWindow, resourceId, vm],
  )
  return <IncidentsScreen vm={vm} actions={actions} />
}

/** Доказательство восстановления продажи: rid и продажа — в адресе окна. */
export function AqsiRecoveryProofBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const saleId = window.params.saleId ?? ''
  const args = useMemo(() => ({ resourceId, saleId }), [resourceId, saleId])
  aqsiRecoveryProof.useQuerySubscription(args, { skip: resourceId === '' || saleId === '' })
  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectRecoveryProofVm()
    const selectProof = aqsiRecoveryProof.select(args)
    return (state: RootState) => selectPackageVm({ resourceId, saleId, proof: selectProof(state) })
  }, [args, resourceId, saleId])
  const vm = useAppSelector(selectVm)
  const actions = useMemo(
    () =>
      createRecoveryProofActions(vm, dispatch, {
        openResolve: (operationId) => openWindow(AQSI_WINDOWS.saleResolve, { rid: resourceId, operationId }),
        close: window.close,
      }),
    [dispatch, openWindow, resourceId, vm, window],
  )
  return <RecoveryProofScreen vm={vm} actions={actions} />
}

/** Журнал расхождений чеков ресурса: предел — намерение окна (вкладка его помнит). */
export function AqsiDiscrepanciesBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const limit = useAppSelector((state: RootState) => discrepancyLimitOf(state.aqsi.incidents, resourceId))
  const args = useMemo(() => ({ resourceId, limit }), [limit, resourceId])
  aqsiReceiptDiscrepancies.useQuerySubscription(args, { skip: resourceId === '' })
  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectDiscrepanciesVm()
    const selectQuery = aqsiReceiptDiscrepancies.select(args)
    return (state: RootState) => selectPackageVm({ resourceId, limit: args.limit, query: selectQuery(state) })
  }, [args, resourceId])
  const vm = useAppSelector(selectVm)
  const actions = useMemo(() => createDiscrepanciesActions(resourceId, dispatch), [dispatch, resourceId])
  return <DiscrepanciesScreen vm={vm} actions={actions} />
}

/** Возврат по номеру чека: rid - из адреса окна, номер - ввод; окно возврата открывается по нему и само скажет, если чека нет. */
export function AqsiRefundLookupBinding() {
  const window = useWindow()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const actions = useMemo(
    () => ({ open: (receiptId: string) => openWindow(AQSI_WINDOWS.refund, { rid: resourceId, key: receiptId }) }),
    [openWindow, resourceId],
  )
  return <RefundLookupScreen actions={actions} />
}

/**
 * «Возврат по слип-чеку» из РМК (RP-FB74): rid - из контекста окна, касса - моя работа за кассой. Слипы и касса - только чтение; «Вернуть» -
 * одна отправка на одно нажатие (новое намерение с новым ключом), без повтора: при неизвестном исходе окно читает кассу и слипы.
 */
export function AqsiSlipRefundBinding() {
  const window = useWindow()
  const resourceId = window.params.rid ?? ''
  const { session: mine } = useMySession(resourceId)
  const seat = mineSeatOf(mine)
  const registerKey = seat?.registerKey ?? null
  const deviceId = seat?.deviceId ?? null
  const sessionVersion = mine?.version ?? null
  const slipsArgs = useMemo(() => (deviceId === null ? null : { resourceId, deviceId }), [deviceId, resourceId])
  const seatArgs = useMemo(
    () => (registerKey === null || deviceId === null ? null : { resourceId, registerKey, deviceId }),
    [deviceId, registerKey, resourceId],
  )
  const slipsSubscription = aqsiSlipRefundSlips.useQuerySubscription(slipsArgs ?? skipToken, { refetchOnMountOrArgChange: true })
  const seatSubscription = aqsiSlipRefundSeat.useQuerySubscription(seatArgs ?? skipToken, { refetchOnMountOrArgChange: true })
  const selectSlips = useMemo(() => (slipsArgs === null ? null : aqsiSlipRefundSlips.select(slipsArgs)), [slipsArgs])
  const selectSeat = useMemo(() => (seatArgs === null ? null : aqsiSlipRefundSeat.select(seatArgs)), [seatArgs])
  const slips = useAppSelector((state: RootState) => selectSlips?.(state))
  const seatState = useAppSelector((state: RootState) => selectSeat?.(state).data)
  const [send, sending] = aqsiSlipRefundSend.useMutation()
  const sendInFlight = useRef(false)
  const seatForVm = useMemo(() => (deviceId === null ? null : { deviceId }), [deviceId])
  const vm = useMemo(
    () => buildSlipRefundVm({ seat: seatForVm, slips, send: sending, seatState }),
    [seatForVm, seatState, sending, slips],
  )
  const actions = useMemo(() => {
    const reread = () => {
      if (slipsArgs !== null) void slipsSubscription.refetch()
      if (seatArgs !== null) void seatSubscription.refetch()
    }
    return {
      openStandard: () => window.replaceWith(AQSI_WINDOWS.refundSearch, { rid: resourceId }),
      close: window.close,
      reload: () => {
        if (slipsArgs !== null) void slipsSubscription.refetch()
      },
      send: (slipId: string, amountKopecks: number) => {
        // Одно нажатие - одна отправка: пока предыдущая не вернулась, вторая не уходит; новое нажатие - новое намерение (ADR-11).
        if (sendInFlight.current || seatArgs === null || sessionVersion === null) return
        sendInFlight.current = true
        void send({ ...seatArgs, originalSlipId: slipId, amountKopecks, intent: newCommandIntent(), expectedVersion: sessionVersion }).finally(() => {
          sendInFlight.current = false
          reread()
        })
      },
      checkStatus: reread,
      startOver: () => {
        sending.reset()
        reread()
      },
    }
  }, [resourceId, seatArgs, seatSubscription, send, sending, sessionVersion, slipsArgs, slipsSubscription, window])
  return <SlipRefundScreen vm={vm} actions={actions} />
}
